import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, Users, Search, Plus, Trash2,
  ShieldAlert,
} from "lucide-react";
import { LOGO_URL } from "../config/constants";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

/*
 * BM-H284 Client Fixer.
 *
 * Deliberately the same page as RefFixerPage: same nav bar, same single
 * glass-card, same look-up input, same two-stage Preview then Confirm.
 * Vanessa asked for this "along the lines of Reference fixer" and meant
 * it literally, so there is no wizard and no new chrome here.
 *
 * The one addition the Reference Fixer does not have is the access panel,
 * because the whole reason for this tool is confidentiality and the
 * operator has to see what the change does NOT protect before confirming.
 */

function getBonsaiUserId() {
  // Canonical session credential, exactly as RefFixerPage reads it.
  return (
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    ""
  );
}

function getUserEmail() {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.email) return u.email;
    }
  } catch (e) {}
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    "";
  if (email) return email;
  const uid = getBonsaiUserId();
  return uid.includes("@") ? uid : "";
}

function getHeaders() {
  return {
    "X-Bonsai-User-Id": getBonsaiUserId(),
    "X-User-Email": getUserEmail(),
    "Content-Type": "application/json",
  };
}

// Shape a fetch error so utils/errorMessage reads the server payload
// rather than falling through to "Couldn't reach the server". Same helper
// and same reasoning as RefFixerPage.
async function buildFetchError(resp) {
  let data = null;
  const text = await resp.text().catch(() => "");
  if (text) {
    try { data = JSON.parse(text); } catch (e) { data = text; }
  }
  const err = new Error(
    (data && typeof data === "object" && (data.detail?.message || data.detail || data.message || data.error))
    || (typeof data === "string" && data)
    || `${resp.status} ${resp.statusText}`
  );
  err.response = { status: resp.status, statusText: resp.statusText, data };
  return err;
}

const blankClient = () => ({ name: "", email: "", client_type: "Individual" });

export default function ClientFixerPage() {
  const navigate = useNavigate();
  const [searchRef, setSearchRef] = useState("");
  const [lookupState, setLookupState] = useState("idle");
  const [lookupMsg, setLookupMsg] = useState("");
  const [matter, setMatter] = useState(null);
  const [access, setAccess] = useState(null);

  // The immediate case is one company becoming two individuals, so the
  // form opens with two empty rows rather than making the operator find
  // an "add" button to do the thing the tool exists for.
  const [clients, setClients] = useState([blankClient(), blankClient()]);
  const [reason, setReason] = useState("");

  // idle -> previewing -> preview_ready -> confirming -> success | error
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null);

  const filledClients = clients.filter((c) => c.name.trim());

  const lookup = async () => {
    if (!searchRef.trim()) return;
    setLookupState("loading");
    setMatter(null);
    setAccess(null);
    setPreview(null);
    setState("idle");
    setMsg("");
    setLookupMsg("");
    try {
      const resp = await fetch(
        `${API}/admin/client-fixer/lookup?reference=${encodeURIComponent(searchRef.trim())}`,
        { method: "GET", headers: getHeaders() }
      );
      if (!resp.ok) throw await buildFetchError(resp);
      const data = await resp.json();
      setMatter(data);
      setAccess(data.access || null);
      setLookupState("done");
      setLookupMsg("");
    } catch (err) {
      setLookupState("error");
      setLookupMsg(errorMessage(err, "Lookup failed"));
    }
  };

  const callReassign = async (dryRun) => {
    const resp = await fetch(`${API}/admin/client-fixer/reassign`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        matter_reference: matter.matter_reference,
        clients: filledClients.map((c) => ({
          name: c.name.trim(),
          email: c.email.trim(),
          client_type: c.client_type || "Individual",
        })),
        reason: reason.trim() || "Client corrected via Client Fixer",
        dry_run: dryRun,
      }),
    });
    if (!resp.ok) throw await buildFetchError(resp);
    return resp.json();
  };

  const doPreview = async () => {
    setState("previewing");
    setMsg("");
    try {
      const data = await callReassign(true);
      setPreview(data);
      setAccess(data.access || access);
      setState("preview_ready");
    } catch (err) {
      setState("error");
      setMsg(errorMessage(err, "Preview failed"));
    }
  };

  const doConfirm = async () => {
    setState("confirming");
    try {
      const data = await callReassign(false);
      const revoked = (data.portals_revoked || []).length;
      setPreview(data);
      setState("success");
      setMsg(
        `Client changed to ${(data.new_clients || []).map((c) => c.name).join(" and ")}.`
        + (revoked ? ` ${revoked} client portal link revoked.` : "")
      );
      // Pipeline caches the old client name; same invalidation signal the
      // Reference Fixer sends.
      sessionStorage.setItem("bonsai_pipeline_cache_invalidated_at", String(Date.now()));
    } catch (err) {
      setState("error");
      setMsg(errorMessage(err, "Change failed"));
    }
  };

  const updateClient = (i, field, value) =>
    setClients((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const addClient = () => setClients((prev) => [...prev, blankClient()]);
  const removeClient = (i) =>
    setClients((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const current = matter?.current;

  return (
    <div className="min-h-screen bg-navy">
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Firm logo" className="w-9 h-9" />
              <div>
                <h1 className="text-base font-bold text-teal leading-tight">
                  Client Fixer
                </h1>
                <p className="text-[10px] text-txt-muted tracking-wide font-medium leading-tight">
                  Matter client correction
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <section className="glass-card p-6 rounded-md space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-teal uppercase tracking-wider flex items-center gap-2 mb-1">
              <Users className="w-4 h-4" /> Matter Client Correction
            </h2>
            <p className="text-xs text-txt-muted">
              Change who the client on an existing matter is, including
              replacing a company with the individuals behind it. Enter one
              row per client. The first row becomes the primary client.
            </p>
          </div>

          {/* Step 1 — look up the matter */}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Matter reference"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              data-testid="client-fixer-search"
              className="w-72 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted font-mono text-sm"
            />
            <Button
              onClick={lookup}
              disabled={lookupState === "loading" || !searchRef.trim()}
              data-testid="client-fixer-lookup"
              className="gap-1.5 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white disabled:opacity-40"
            >
              {lookupState === "loading"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Search className="w-4 h-4" />}
              Look up
            </Button>
            {lookupMsg && (
              <span
                className="text-xs flex items-center gap-1 text-status-red"
                data-testid="client-fixer-lookup-error"
              >
                <XCircle className="w-3.5 h-3.5" />
                {lookupMsg}
              </span>
            )}
          </div>

          {/* Step 2 — current client, then the new ones */}
          {current && (
            <div className="space-y-4" data-testid="client-fixer-matter">
              <div className="rounded-md border border-navy-border/60 bg-navy p-3 text-xs">
                <p className="text-txt-muted">Current client on <span className="font-mono text-txt">{matter.matter_reference}</span></p>
                <p className="text-txt font-semibold mt-1" data-testid="client-fixer-current-name">
                  {current.primary_name || "(none recorded)"}
                  {current.client_type ? ` · ${current.client_type}` : ""}
                </p>
                {current.joint_clients?.length > 0 && (
                  <p className="text-txt-muted mt-0.5">
                    Joint: {current.joint_clients.map((j) => j.name).join(", ")}
                  </p>
                )}
                {current.read_from && (
                  <p className="text-txt-faint mt-1 italic">Read from {current.read_from}</p>
                )}
              </div>

              <div className="space-y-2">
                {clients.map((c, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`client-row-${i}`}>
                    <span className="text-[10px] uppercase tracking-wide text-txt-muted w-16">
                      {i === 0 ? "Primary" : `Joint ${i}`}
                    </span>
                    <Input
                      placeholder="Full name"
                      value={c.name}
                      onChange={(e) => updateClient(i, "name", e.target.value)}
                      data-testid={`client-row-${i}-name`}
                      className="w-56 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted text-sm"
                    />
                    <Input
                      placeholder="Email (optional)"
                      value={c.email}
                      onChange={(e) => updateClient(i, "email", e.target.value)}
                      data-testid={`client-row-${i}-email`}
                      className="w-56 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted text-sm"
                    />
                    <select
                      value={c.client_type}
                      onChange={(e) => updateClient(i, "client_type", e.target.value)}
                      data-testid={`client-row-${i}-type`}
                      className="bg-navy-card border border-navy-border rounded-md text-txt text-sm px-2 py-2"
                    >
                      <option value="Individual">Individual</option>
                      <option value="Company">Company</option>
                    </select>
                    {clients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeClient(i)}
                        aria-label={`Remove client ${i + 1}`}
                        className="text-txt-muted hover:text-status-red"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addClient}
                  data-testid="client-fixer-add"
                  className="gap-1.5 text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal"
                >
                  <Plus className="w-3.5 h-3.5" /> Add another client
                </Button>
              </div>

              <Input
                placeholder="Reason (recorded in the audit log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="client-fixer-reason"
                className="w-full bg-navy-card border-navy-border text-txt placeholder:text-txt-muted text-sm"
              />

              <div className="flex flex-wrap items-center gap-2">
                {(state === "idle" || state === "previewing" || state === "error") && (
                  <Button
                    onClick={doPreview}
                    disabled={state === "previewing" || filledClients.length === 0}
                    data-testid="client-fixer-preview"
                    className="gap-1.5 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white disabled:opacity-40"
                  >
                    {state === "previewing" && <Loader2 className="w-4 h-4 animate-spin" />}
                    Preview
                  </Button>
                )}
                {(state === "preview_ready" || state === "confirming") && (
                  <>
                    <Button
                      onClick={doConfirm}
                      disabled={state === "confirming"}
                      data-testid="client-fixer-confirm"
                      className="gap-1.5 bg-teal text-white hover:bg-teal/90 disabled:opacity-40"
                    >
                      {state === "confirming" && <Loader2 className="w-4 h-4 animate-spin" />}
                      Confirm change
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setState("idle"); setPreview(null); }}
                      data-testid="client-fixer-cancel"
                      className="text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal"
                    >
                      Cancel
                    </Button>
                  </>
                )}
                {state === "success" && (
                  <span className="text-xs text-teal flex items-center gap-1" data-testid="client-fixer-success">
                    <CheckCircle className="w-3.5 h-3.5" /> {msg}
                  </span>
                )}
                {state === "error" && msg && (
                  <span className="text-xs text-status-red flex items-center gap-1" data-testid="client-fixer-error">
                    <XCircle className="w-3.5 h-3.5" /> {msg}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Preview panel — same information density as the Ref Fixer's */}
          {preview && (state === "preview_ready" || state === "success") && (
            <div
              className="rounded-md border border-navy-border/60 bg-navy p-3 text-xs text-txt-muted space-y-2"
              data-testid="client-fixer-preview-panel"
            >
              <p className="font-semibold text-txt">
                {state === "success" ? "Applied" : "Planned changes"}: {current?.primary_name} → {filledClients.map((c) => c.name).join(" and ")}
              </p>

              {preview.planned?.collections?.length > 0 && (
                <ul className="space-y-0.5">
                  {preview.planned.collections.map((c) => (
                    <li key={c.name} className="font-mono">
                      · {c.name}: {c.match_count} record{c.match_count === 1 ? "" : "s"}
                      {c.carries_joint_clients ? " (holds joint clients)" : ""}
                    </li>
                  ))}
                </ul>
              )}

              {preview.planned?.portals_to_revoke?.length > 0 && (
                <p className="text-status-amber" data-testid="client-fixer-portal-warning">
                  {preview.planned.portals_to_revoke.length} client portal link issued
                  to the current client will be revoked and will stop working.
                </p>
              )}

              {preview.planned?.untouched?.filter((u) => u.match_count > 0).length > 0 && (
                <div>
                  <p className="text-txt font-semibold">Deliberately not changed</p>
                  <ul className="space-y-0.5">
                    {preview.planned.untouched
                      .filter((u) => u.match_count > 0)
                      .map((u) => (
                        <li key={u.name}>· {u.name} ({u.match_count}): {u.reason}</li>
                      ))}
                  </ul>
                </div>
              )}

              {preview.aml_attention && (
                <p className="text-status-amber flex items-start gap-1" data-testid="client-fixer-aml">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {preview.aml_attention.note}
                </p>
              )}
            </div>
          )}

          {/* The honest access statement. Shown before confirming, because
              believing a rename protected something it did not would be
              worse than not doing it at all. */}
          {access && (
            <div
              className="rounded-md border border-navy-border/60 bg-navy p-3 text-xs space-y-2"
              data-testid="client-fixer-access"
            >
              <p className="text-txt font-semibold">What this change does</p>
              <ul className="space-y-0.5 text-txt-muted">
                {access.changes.map((line, i) => <li key={i}>· {line}</li>)}
              </ul>
              <p className="text-txt font-semibold">What it does not do</p>
              <ul className="space-y-0.5 text-txt-muted">
                {access.does_not_change.map((line, i) => <li key={i}>· {line}</li>)}
              </ul>
            </div>
          )}
        </section>

        <p className="text-xs text-txt-muted text-center mt-6">
          Logged in as {getUserEmail()}
        </p>
      </main>
    </div>
  );
}
